import { Badge, Box, IconButton, Popover, Typography, useMediaQuery, useTheme } from '@mui/material';
import { MessageCircle, RefreshCw, Send, X } from "lucide-react";

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import useWebSocket from '@/hook/useWebSocket';
import {
  CHAT_EMOJIS,
  MAX_MESSAGE_HISTORY,
  MAX_MESSAGE_LENGTH,
  containsLinks,
  getUsernameColor,
  removeLinks,
} from '@/utils/chat';

import useStyles from './index.styles';
import { designTokens } from "@/shared/styles/tokens";
import LoadingIndicator from "@/shared/ui/LoadingIndicator";

interface SideChatPanelProps {
  // No props needed since it manages its own state
}

const SideChatPanel: React.FC<SideChatPanelProps> = () => {
  const { classes } = useStyles();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState('');
  const [emojiAnchorEl, setEmojiAnchorEl] = useState<HTMLElement | null>(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [lastReadMessageId, setLastReadMessageId] = useState<number | null>(null);
  const [linkWarning, setLinkWarning] = useState(false);

  // Get user profile for family comparison and localStorage key
  const { myProfile } = useAppSelector((state) => state.profile);
  const { account } = useAppSelector((state) => state.auth);

  // Generate localStorage key based on user account
  const getStorageKey = () => {
    const userKey = myProfile?.id || account || 'anonymous';
    return `chat_last_read_${userKey}`;
  };

  // Load last read message ID from localStorage on component mount
  useEffect(() => {
    const storageKey = getStorageKey();
    const savedLastReadId = localStorage.getItem(storageKey);
    if (savedLastReadId) {
      const parsedId = parseInt(savedLastReadId, 10);
      if (!isNaN(parsedId)) {
        setLastReadMessageId(parsedId);
      }
    }
  }, [myProfile?.id, account]);

  // Save last read message ID to localStorage whenever it changes
  useEffect(() => {
    if (lastReadMessageId !== null) {
      const storageKey = getStorageKey();
      localStorage.setItem(storageKey, lastReadMessageId.toString());
    }
  }, [lastReadMessageId, myProfile?.id, account]);

  // Clear link warning after 3 seconds
  useEffect(() => {
    if (linkWarning) {
      const timer = setTimeout(() => {
        setLinkWarning(false);
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [linkWarning]);

  // Common emojis for the picker
  const emojis = CHAT_EMOJIS;

  // Use WebSocket hook
  const {
    messages,
    connectionStatus,
    sendMessage: sendWebSocketMessage,
    reconnect
  } = useWebSocket();

  // Auto-scroll to bottom when new messages arrive
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (isOpen) {
      messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages, isOpen]);

  // Handle unread messages count with localStorage persistence
  useEffect(() => {
    if (messages.length === 0) {
      setUnreadCount(0);
      return;
    }

    if (isOpen) {
      // When chat is open, mark all messages as read
      const latestMessageId = messages[0]?.id; // messages are reversed, so first is latest
      if (latestMessageId && latestMessageId !== lastReadMessageId) {
        setLastReadMessageId(latestMessageId);
        setUnreadCount(0);
      }
    } else {
      // When chat is closed, count unread messages
      if (lastReadMessageId === null) {
        // First time or no saved data - consider all messages as unread
        setUnreadCount(messages.length);
      } else {
        // Count messages newer than last read (messages with higher ID)
        const unreadMessages = messages.filter(msg => msg.id > lastReadMessageId);
        setUnreadCount(unreadMessages.length);
      }
    }
  }, [messages, isOpen, lastReadMessageId]);

  // Handle chat open
  const handleChatOpen = () => {
    setIsOpen(true);
    // Mark messages as read when opening chat
    if (messages.length > 0) {
      const latestMessageId = messages[0]?.id;
      if (latestMessageId) {
        setLastReadMessageId(latestMessageId);
        setUnreadCount(0);
      }
    }
  };


  // Handle input change with character limit and link detection
  const handleMessageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = e.target.value;

    // Check character limit
    if (newValue.length <= MAX_MESSAGE_LENGTH) {
      // Check for links and show warning
      if (containsLinks(newValue) && !containsLinks(message)) {
        setLinkWarning(true);
      }
      setMessage(newValue);
    }
  };

  // Handle form submission with link filtering
  const handleSendMessage = (e?: React.FormEvent) => {
    if (e) {
      e.preventDefault();
    }

    if (!message.trim()) return;

    let messageText = message.trim();

    // Check for links and remove them
    if (containsLinks(messageText)) {
      messageText = removeLinks(messageText);
      setLinkWarning(true);

      // If message becomes empty after link removal, don't send
      if (!messageText.trim() || messageText.trim() === '[LINK REMOVED]') {
        setMessage('');
        return;
      }
    }

    if (connectionStatus === 'connected') {
      // Send via WebSocket with cleaned message
      const sent = sendWebSocketMessage(messageText);
      if (sent) {
        setMessage('');
        // Focus back to input after sending
        inputRef.current?.focus();
      } else {
        console.warn('Failed to send message - check length or connection');
      }
    } else {
      // Show error or try to reconnect
      console.warn('Cannot send message: WebSocket not connected');
    }
  };

  // Handle emoji selection with character limit check
  const handleEmojiClick = (emoji: string) => {
    const input = inputRef.current;
    if (input) {
      const cursorPosition = input.selectionStart || message.length;
      const newMessage =
        message.substring(0, cursorPosition) +
        emoji +
        message.substring(cursorPosition);

      // Check character limit
      if (newMessage.length <= MAX_MESSAGE_LENGTH) {
        setMessage(newMessage);

        // Set cursor position after emoji
        setTimeout(() => {
          input.focus();
          input.setSelectionRange(cursorPosition + emoji.length, cursorPosition + emoji.length);
        }, 0);
      }
    } else {
      const newMessage = message + emoji;
      if (newMessage.length <= MAX_MESSAGE_LENGTH) {
        setMessage(newMessage);
      }
    }

    // Close emoji panel
    setEmojiAnchorEl(null);
  };

  // Handle emoji panel toggle
  const handleEmojiPanelToggle = (event: React.MouseEvent<HTMLElement>) => {
    if (emojiAnchorEl) {
      setEmojiAnchorEl(null);
    } else {
      setEmojiAnchorEl(event.currentTarget);
    }
  };

  // Handle send button click
  const handleSendButtonClick = () => {
    handleSendMessage();
  };

  // Handle reconnect button click
  const handleReconnect = () => {
    reconnect();
  };

  // Handle clear input (renamed from handleClearMessages)
  const handleClearInput = () => {
    setMessage('');
    setLinkWarning(false);
    // Focus back to input after clearing
    inputRef.current?.focus();
  };

  // Get connection status color
  const getConnectionStatusColor = () => {
    switch (connectionStatus) {
      case 'connected': return theme.design.color.status.success;
      case 'connecting': return theme.design.color.status.warning;
      case 'disconnected': return theme.design.color.status.danger;
      default: return theme.design.color.text.secondary;
    }
  };

  // Get connection status text
  const getConnectionStatusText = () => {
    switch (connectionStatus) {
      case 'connected': return 'Connected';
      case 'connecting': return 'Connecting...';
      case 'disconnected': return 'Disconnected';
      default: return 'Unknown';
    }
  };

  // Get character count color based on remaining characters
  const getCharCountColor = () => {
    const remaining = MAX_MESSAGE_LENGTH - message.length;
    if (remaining <= 10) return theme.design.color.status.danger; // Red when close to limit
    if (remaining <= 30) return theme.design.color.status.warning; // Orange when getting close
    return theme.design.color.text.muted; // Default gray
  };

  // Get chat toggle button position based on route and device
  const getChatTogglePosition = () => {
    const isMapRoute = location.pathname.startsWith('/map');
    
    if (isMapRoute) {
      if (isMobile) {
        return {
          bottom: "16px",
          right: "80px",
        };
      } else {
        return {
          bottom: "100px", // 20px + 80px up
          right: "20px",
        };
      }
    }
    
    // Default position for non-map routes
    return {
      bottom: "20px",
      right: "20px",
    };
  };

  return (
    <>
      {/* Chat Toggle Button */}
      <Box sx={{
        position: "fixed",
        ...getChatTogglePosition(),
      }}>
        <Badge
          badgeContent={unreadCount}
          color="error"
          sx={{
            '& .MuiBadge-badge': {
              fontSize: '10px',
              minWidth: '24px',
              height: '24px',
              borderRadius: '12px',
              backgroundColor: theme.design.color.status.danger,
              color: designTokens.color.text.primary,
              fontFamily: theme.design.typography.fontFamily,
              fontWeight: 600,
              position: "absolute",
              right: "8px",
              top: "8px",
              zIndex: 1,
            }
          }}
          invisible={unreadCount === 0}
        >
          <IconButton
            className={classes.chatToggleButton}
            onClick={handleChatOpen}
            style={{ display: isOpen ? 'none' : 'flex', zIndex: 0 }}
          >
            <MessageCircle />
          </IconButton>
        </Badge>
      </Box>

      {/* Chat Drawer */}
      <Box
        className={`${classes.chatDrawer} ${isOpen ? classes.chatDrawerOpen : ''}`}
      >
        <Box className={classes.chatHeader}>
          <Typography className={classes.chatTitle}>
            MAFIA Chat
            <Typography
              component="span"
              sx={{
                ml: 1,
                fontSize: '12px',
                color: getConnectionStatusColor()
              }}
            >
              ({getConnectionStatusText()})
            </Typography>
            {messages.length > 0 && (
              <Typography
                component="span"
                sx={{
                  ml: 1,
                  fontSize: '10px',
                  color: theme.design.color.text.secondary
                }}
              >
                ({messages.length}/{MAX_MESSAGE_HISTORY})
              </Typography>
            )}
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            {/* Reconnect button */}
            {connectionStatus === 'disconnected' && (
              <IconButton
                onClick={handleReconnect}
                sx={{
                  color: designTokens.color.text.primary,
                  padding: '4px',
                  '&:hover': { backgroundColor: theme.design.color.overlay.white10 }
                }}
                title="Reconnect"
              >
                <RefreshCw size={16} />
              </IconButton>
            )}

            {/* Close button */}
            <IconButton
              className={classes.chatCloseButton}
              onClick={() => setIsOpen(false)}
            >
              <X />
            </IconButton>
          </Box>
        </Box>

        <Box className={classes.chatMessages}>
          {messages.length === 0 ? (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              color: theme.design.color.text.muted,
              fontSize: '14px',
              fontFamily: theme.design.typography.fontFamily,
              textAlign: 'center',
              gap: 2
            }}>
              {connectionStatus === 'connecting' && (
                <LoadingIndicator size="small" label="Loading chat" />
              )}
              <Typography sx={{ fontSize: 'inherit', color: 'inherit' }}>
                {connectionStatus === 'connecting' ? 'Loading chat...' :
                  connectionStatus === 'disconnected' ? 'Chat disconnected' :
                    'No messages yet'}
              </Typography>

              {connectionStatus === 'disconnected' && (
                <Box
                  onClick={handleReconnect}
                  sx={{
                    cursor: 'pointer',
                    color: theme.design.color.status.success,
                    fontSize: '12px',
                    textDecoration: 'underline',
                    '&:hover': { color: theme.design.color.action.primaryHover }
                  }}
                >
                  Click to reconnect
                </Box>
              )}
            </Box>
          ) : (
            <Box sx={{ width: '100%', height: '100%', overflowY: 'auto', display: "flex", flexDirection: "column", gap: "8px" }}>
              {/* Messages */}
              {[...messages].reverse().map((msg, index) => (
                <Typography
                  key={msg.id}
                  sx={{
                    lineBreak: "auto",
                  }}
                >
                  {/* Time */}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '9px',
                      color: theme.design.color.border.popover,
                      fontFamily: theme.design.typography.fontFamily,
                      alignSelf: 'center',
                      width: 'min-content',
                      minWidth: '48px',
                    }}>
                    {msg.timestamp}
                  </Typography>

                  {/* Family */}
                  <Typography
                    component="a"
                    href={`/family/${msg.family}`}
                    sx={{
                      fontSize: '11px',
                      color: theme.design.color.text.muted,
                      fontFamily: theme.design.typography.fontFamily,
                      fontWeight: 500,
                      alignSelf: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: 'min-content',
                      marginLeft: '4px',
                      textDecoration: 'none'
                    }}>
                    {msg.family || 'No Family'}
                  </Typography>

                  {/* Username */}
                  <Typography
                    component="a"
                    href={`/profile/${msg.username}`}
                    sx={{
                      fontSize: '12px',
                      color: getUsernameColor(msg),
                      fontFamily: theme.design.typography.fontFamily,
                      fontWeight: 600,
                      alignSelf: 'center',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      width: 'min-content',
                      marginLeft: '4px',
                      textDecoration: 'none'
                    }}>
                    {msg.username}
                  </Typography>

                  {/* Message */}
                  <Typography
                    component="span"
                    sx={{
                      fontSize: '13px',
                      color: theme.design.color.text.primary,
                      fontFamily: theme.design.typography.fontFamily,
                      lineHeight: '1.4',
                      alignSelf: 'center',
                      wordWrap: 'break-word',
                      overflowWrap: 'break-word',
                      width: 'min-content',
                      marginLeft: '8px'
                    }}>
                    {msg.message}
                  </Typography>
                </Typography>
              ))}
              <div ref={messagesEndRef} />
            </Box>
          )}
        </Box>

        <Box className={classes.chatInputContainer}>
          <form onSubmit={handleSendMessage} style={{ width: '100%' }}>
            <Box className={classes.chatInputWrapper}>
              <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center'
              }}>
                <Typography className={classes.chatInputLabel}>
                  Message:
                </Typography>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  {/* Link warning */}
                  {linkWarning && (
                    <Typography
                      sx={{
                        fontSize: '9px',
                        color: theme.design.color.status.danger,
                        fontFamily: theme.design.typography.fontFamily,
                        fontWeight: 600,
                        animation: 'pulse 1s infinite'
                      }}
                    >
                      No links allowed
                    </Typography>
                  )}

                  {/* Character count */}
                  <Typography
                    sx={{
                      fontSize: '10px',
                      color: getCharCountColor(),
                      fontFamily: theme.design.typography.fontFamily,
                      fontWeight: 500
                    }}
                  >
                    {message.length}/{MAX_MESSAGE_LENGTH}
                  </Typography>

                  {/* Clear input button - only show when there's text */}
                  {message.trim().length > 0 && (
                    <Typography
                      onClick={handleClearInput}
                      sx={{
                        fontSize: '10px',
                        color: theme.design.color.text.muted,
                        cursor: 'pointer',
                        textDecoration: 'underline',
                        '&:hover': { color: theme.design.color.text.primary }
                      }}
                    >
                      Clear
                    </Typography>
                  )}
                </Box>
              </Box>

              <Box className={classes.chatInputBox}>
                <input
                  ref={inputRef}
                  type="text"
                  className={classes.chatInput}
                  placeholder={
                    connectionStatus === 'connected' ? `${MAX_MESSAGE_LENGTH} chars max. No links allowed.` :
                      connectionStatus === 'connecting' ? "Connecting..." :
                        "Disconnected"
                  }
                  value={message}
                  onChange={handleMessageChange}
                  maxLength={MAX_MESSAGE_LENGTH}
                  disabled={connectionStatus !== 'connected'}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleSendMessage();
                    }
                  }}
                  style={{
                    borderColor: containsLinks(message) ? theme.design.color.status.danger : undefined
                  }}
                />

                {/* Emoji Button */}
                <span
                  className={classes.chatEmoji}
                  onClick={handleEmojiPanelToggle}
                  style={{
                    cursor: connectionStatus === 'connected' ? 'pointer' : 'not-allowed',
                    opacity: connectionStatus === 'connected' ? 1 : 0.5,
                    userSelect: 'none'
                  }}
                >
                  😊
                </span>

                {/* Send Button */}
                <IconButton
                  onClick={handleSendButtonClick}
                  disabled={connectionStatus !== 'connected' || !message.trim()}
                  sx={{
                    padding: '4px',
                    marginLeft: '4px',
                    color: connectionStatus === 'connected' && message.trim() ? theme.design.color.status.success : theme.design.color.text.secondary,
                    '&:hover': {
                      backgroundColor: connectionStatus === 'connected' && message.trim() ? theme.design.color.overlay.white10 : designTokens.color.overlay.transparent
                    },
                    '&:disabled': {
                      color: theme.design.color.text.secondary
                    },
                    transition: 'all 0.2s ease'
                  }}
                  title="Send message"
                >
                  <Send size={16} />
                </IconButton>
              </Box>
            </Box>
          </form>
        </Box>

        {/* Emoji Picker Popover */}
        <Popover
          open={Boolean(emojiAnchorEl)}
          anchorEl={emojiAnchorEl}
          onClose={() => setEmojiAnchorEl(null)}
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'center',
          }}
          transformOrigin={{
            vertical: 'bottom',
            horizontal: 'center',
          }}
          PaperProps={{
            sx: {
              backgroundColor: theme.design.color.surface.popover,
              border: `1px solid ${theme.design.color.border.popover}`,
              borderRadius: '8px',
              padding: '8px',
              maxWidth: '280px',
              boxShadow: theme.design.shadow.md
            }
          }}
        >
          <Box sx={{
            display: 'grid',
            gridTemplateColumns: 'repeat(8, 1fr)',
            gap: '4px',
            maxHeight: '200px',
            overflowY: 'auto'
          }}>
            {emojis.map((emoji, index) => (
              <Box
                key={index}
                onClick={() => handleEmojiClick(emoji)}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  width: '28px',
                  height: '28px',
                  cursor: 'pointer',
                  borderRadius: '4px',
                  fontSize: '18px',
                  transition: 'background-color 0.2s ease',
                  '&:hover': {
                    backgroundColor: theme.design.color.border.popover
                  }
                }}
              >
                {emoji}
              </Box>
            ))}
          </Box>
        </Popover>
      </Box>

      {/* Overlay */}
      {isOpen && (
        <Box
          className={classes.chatOverlay}
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
};

export default SideChatPanel;
